import { zodResolver } from '@hookform/resolvers/zod';
import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { ReactNode } from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';

// Ligne unique de l'organisation (tenant). ID fixe défini au seed.
const ORG_ID = '00000000-0000-0000-0000-000000000001';

const schema = z.object({
  raison_sociale: z.string().min(1, 'La raison sociale est obligatoire'),
  forme_juridique: z.string(),
  siren: z.string(),
  siret_siege: z.string(),
  tva_intracom: z.string(),
  naf: z.string(),
  rcs: z.string(),
  capital_social: z.string(),
  adresse_ligne1: z.string(),
  adresse_ligne2: z.string(),
  code_postal: z.string(),
  ville: z.string(),
  telephone: z.string(),
  email: z.union([z.string().email('Email invalide'), z.literal('')]),
  site_web: z.string(),
  decennale_compagnie: z.string(),
  decennale_numero_police: z.string(),
  decennale_zone_couverture: z.string(),
  mediateur_nom: z.string(),
  mediateur_url: z.string(),
  qualifications: z.string(),
  iban: z.string(),
  bic: z.string(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = {
  raison_sociale: '',
  forme_juridique: '',
  siren: '',
  siret_siege: '',
  tva_intracom: '',
  naf: '',
  rcs: '',
  capital_social: '',
  adresse_ligne1: '',
  adresse_ligne2: '',
  code_postal: '',
  ville: '',
  telephone: '',
  email: '',
  site_web: '',
  decennale_compagnie: '',
  decennale_numero_police: '',
  decennale_zone_couverture: '',
  mediateur_nom: '',
  mediateur_url: '',
  qualifications: '',
  iban: '',
  bic: '',
};

async function fetchOrganisation(): Promise<Record<string, unknown>> {
  const { data, error } = await supabase
    .from('organizations')
    .select('*')
    .eq('id', ORG_ID)
    .single();
  if (error) throw new Error(error.message);
  return data as Record<string, unknown>;
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

export function SocietePage() {
  const queryClient = useQueryClient();
  const orgQuery = useQuery({ queryKey: ['organisation'], queryFn: fetchOrganisation });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  // Remplit le formulaire dès que les données de l'organisation arrivent.
  useEffect(() => {
    const data = orgQuery.data;
    if (!data) return;
    const quals = Array.isArray(data.qualifications)
      ? (data.qualifications as unknown[]).map(str).filter(Boolean).join(', ')
      : '';
    reset({
      raison_sociale: str(data.raison_sociale),
      forme_juridique: str(data.forme_juridique),
      siren: str(data.siren),
      siret_siege: str(data.siret_siege),
      tva_intracom: str(data.tva_intracom),
      naf: str(data.naf),
      rcs: str(data.rcs),
      capital_social: str(data.capital_social),
      adresse_ligne1: str(data.adresse_ligne1),
      adresse_ligne2: str(data.adresse_ligne2),
      code_postal: str(data.code_postal),
      ville: str(data.ville),
      telephone: str(data.telephone),
      email: str(data.email),
      site_web: str(data.site_web),
      decennale_compagnie: str(data.decennale_compagnie),
      decennale_numero_police: str(data.decennale_numero_police),
      decennale_zone_couverture: str(data.decennale_zone_couverture),
      mediateur_nom: str(data.mediateur_nom),
      mediateur_url: str(data.mediateur_url),
      qualifications: quals,
      iban: str(data.iban),
      bic: str(data.bic),
    });
  }, [orgQuery.data, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload = {
        ...values,
        email: values.email === '' ? null : values.email,
        qualifications: values.qualifications
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const { error } = await supabase.from('organizations').update(payload).eq('id', ORG_ID);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organisation'] });
    },
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values);
  });

  if (orgQuery.isLoading) {
    return <p className="text-muted-foreground">Chargement des paramètres…</p>;
  }
  if (orgQuery.isError) {
    return (
      <p className="text-destructive">
        Impossible de charger les paramètres société : {messageErreur(orgQuery.error)}
      </p>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Paramètres société</h1>
          <p className="text-muted-foreground">
            Ces informations alimentent vos devis et factures (mentions légales obligatoires).
          </p>
        </div>
        <Button type="submit" disabled={mutation.isPending || !isDirty}>
          {mutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </div>

      {mutation.isSuccess ? (
        <div className="rounded-md border border-green-600/30 bg-green-600/10 px-4 py-2 text-sm text-green-700 dark:text-green-400">
          Modifications enregistrées.
        </div>
      ) : null}
      {mutation.isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          Échec de l'enregistrement : {messageErreur(mutation.error)}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Identité</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Raison sociale *" htmlFor="raison_sociale" error={errors.raison_sociale?.message}>
            <Input id="raison_sociale" {...register('raison_sociale')} />
          </Field>
          <Field label="Forme juridique" htmlFor="forme_juridique" error={undefined}>
            <Input id="forme_juridique" placeholder="SARL, SAS…" {...register('forme_juridique')} />
          </Field>
          <Field label="SIREN" htmlFor="siren" error={undefined}>
            <Input id="siren" {...register('siren')} />
          </Field>
          <Field label="SIRET (siège)" htmlFor="siret_siege" error={undefined}>
            <Input id="siret_siege" {...register('siret_siege')} />
          </Field>
          <Field label="N° TVA intracom." htmlFor="tva_intracom" error={undefined}>
            <Input id="tva_intracom" {...register('tva_intracom')} />
          </Field>
          <Field label="Code NAF/APE" htmlFor="naf" error={undefined}>
            <Input id="naf" {...register('naf')} />
          </Field>
          <Field label="RCS" htmlFor="rcs" error={undefined}>
            <Input id="rcs" {...register('rcs')} />
          </Field>
          <Field label="Capital social" htmlFor="capital_social" error={undefined}>
            <Input id="capital_social" {...register('capital_social')} />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Coordonnées</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Adresse" htmlFor="adresse_ligne1" error={undefined}>
            <Input id="adresse_ligne1" {...register('adresse_ligne1')} />
          </Field>
          <Field label="Complément d'adresse" htmlFor="adresse_ligne2" error={undefined}>
            <Input id="adresse_ligne2" {...register('adresse_ligne2')} />
          </Field>
          <Field label="Code postal" htmlFor="code_postal" error={undefined}>
            <Input id="code_postal" {...register('code_postal')} />
          </Field>
          <Field label="Ville" htmlFor="ville" error={undefined}>
            <Input id="ville" {...register('ville')} />
          </Field>
          <Field label="Téléphone" htmlFor="telephone" error={undefined}>
            <Input id="telephone" {...register('telephone')} />
          </Field>
          <Field label="Email" htmlFor="email" error={errors.email?.message}>
            <Input id="email" type="email" {...register('email')} />
          </Field>
          <Field label="Site web" htmlFor="site_web" error={undefined}>
            <Input id="site_web" {...register('site_web')} />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Assurance &amp; mentions légales</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Assurance décennale — compagnie" htmlFor="decennale_compagnie" error={undefined}>
            <Input id="decennale_compagnie" {...register('decennale_compagnie')} />
          </Field>
          <Field label="N° de police décennale" htmlFor="decennale_numero_police" error={undefined}>
            <Input id="decennale_numero_police" {...register('decennale_numero_police')} />
          </Field>
          <Field label="Zone de couverture décennale" htmlFor="decennale_zone_couverture" error={undefined}>
            <Input id="decennale_zone_couverture" placeholder="France métropolitaine" {...register('decennale_zone_couverture')} />
          </Field>
          <Field label="Qualifications (RGE, Qualibat…)" htmlFor="qualifications" error={undefined}>
            <Input id="qualifications" placeholder="RGE, Qualibat 3212…" {...register('qualifications')} />
          </Field>
          <Field label="Médiateur de la consommation" htmlFor="mediateur_nom" error={undefined}>
            <Input id="mediateur_nom" {...register('mediateur_nom')} />
          </Field>
          <Field label="URL du médiateur" htmlFor="mediateur_url" error={undefined}>
            <Input id="mediateur_url" {...register('mediateur_url')} />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Coordonnées bancaires</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="IBAN" htmlFor="iban" error={undefined}>
            <Input id="iban" {...register('iban')} />
          </Field>
          <Field label="BIC" htmlFor="bic" error={undefined}>
            <Input id="bic" {...register('bic')} />
          </Field>
        </CardContent>
      </Card>
    </form>
  );
}
